import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { ICmsClientExtensionLoaderService } from '@icms/client/extension-loader/icms-client-extension-loader.service';
import { ICmsDraftResource } from '@icms/core/resource/draft/icms-draft.resource';
import { BehaviorSubject } from 'rxjs';
import { ICmsCampaign } from '../../resource/campaign/icms-campaign';
import { ICmsCampaignResource } from '../../resource/campaign/icms-campaign.resource';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';

const localStorageKey = 'icms-campaign';

declare module '@df/core/app' {
    interface App {
        iCmsCampaignsService: ICmsCampaignsService;
    }
}

@Injectable({ providedIn: 'root' })
export class ICmsCampaignsService {
    /**
     * Hash parameter name
     */
    private readonly hashName = '_campaign';

    readonly currentCampaign$ = new BehaviorSubject<ICmsCampaign | undefined>(undefined);

    constructor(
        app: App,
        private client: Client,
        private iCmsCampaignResource: ICmsCampaignResource,
        private iCmsDraftResource: ICmsDraftResource,
        private hashStorageService: HashStorageService,
        private sessionStorage: SessionStorageService,
        router: Router,
        iCmsClientExtensionLoaderService: ICmsClientExtensionLoaderService
    ) {
        app.iCmsCampaignsService = this;
        if (app.isPlatformBrowser) {
            this.load();
        }
        this.currentCampaign$.subscribe(campaign => {
            this.sessionStorage.set(this.hashName, campaign?.data?.hash);
            iCmsClientExtensionLoaderService.campaign$.next(campaign);
        });
        if (router.navigated) {
            this.hashStorageService.remove(this.hashName, true);
        } else {
            const subscription = router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.hashStorageService.remove(this.hashName, true);
                    subscription.unsubscribe();
                }
            });
        }
    }

    /**
     * Gets public link to activate draft
     */
    getShareLink(campaign: ICmsCampaign) {
        const url = location.protocol + '//' + location.host + location.pathname;
        return `${url}#${this.hashName}=${campaign.data?.hash || ''}`;
    }

    reset() {
        if (this.currentCampaign$.value) {
            this.activate(undefined);
        }
    }

    async activate(campaign: ICmsCampaign | undefined) {
        if (campaign) {
            await campaign.loadDrafts();
        }
        const prev = this.currentCampaign$.value;
        if (prev && !this.client.isCms) {
            prev.drafts.forEach(draft => {
                draft.target.setDraft(undefined);
            });
        }
        this.currentCampaign$.next(campaign);
        if (!this.client.isCms) {
            if (campaign) {
                campaign.drafts.forEach(async draft => {
                    await draft.target.load(3);
                    draft.target.setDraft(draft);
                });
            } else {
                this.iCmsDraftResource.getAllExisting().forEach(i => {
                    if (i.target.getDraft()) {
                        i.target.setDraft(undefined);
                    }
                });
            }
        }
    }

    deactivate(campaign: ICmsCampaign) {
        if (campaign === this.currentCampaign$.value) {
            this.reset();
        }
    }

    private async load() {
        const draftHash = this.hashStorageService.get(this.hashName) || this.sessionStorage.get(this.hashName);
        if (draftHash) {
            const campaign = await this.iCmsCampaignResource.findOne({ filter: { hash: draftHash }, verbosity: 1 });
            await campaign?.load(1);
            this.activate(campaign);
        }
    }
}
